import {
  Box,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material'
import {
  SectorComercialGruposFilterDTOFields,
  SectorComercialGrupoViewDTO,
  SectorComercialGrupoViewDTOFields,
  SectorComercialViewDTO,
  SectorComercialViewDTOFields
} from './types'
import {DataGrid, GridColDef, GridColumnMenuProps, GridRowSelectionModel} from '@mui/x-data-grid'
import {useEffect, useState} from 'react'
import {HttpSectorComercialABM} from './api'
import {EntityWithIdFields} from 'src/types'
import {Button} from 'src/components/forms'
import {FilterAlt, FilterAltOff} from '@mui/icons-material'
import {z} from 'zod'
import {ControlledAutocomplete} from 'src/components/forms/ControlledAutocomplete'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {HttpSystems} from 'src/http/workflow/httpSystems'
import {CountryAvatar} from "../../../../../components/icons/CountryAvatar";

export type EditSectorComercialProps = {
  sector: SectorComercialViewDTO
  onClose: () => void
}

const schemaFiltroGrupos = z.object({
  [SectorComercialGruposFilterDTOFields.CodsPais]: z.array(z.number()).optional(),
  [SectorComercialGruposFilterDTOFields.CodsMensajeTipo]: z.array(z.number()).optional()
})

type SchemaFiltroGrupos = z.infer<typeof schemaFiltroGrupos>

function EditSectorComercial({sector, onClose}: EditSectorComercialProps) {
  const [groups, setGroups] = useState<SectorComercialGrupoViewDTO[]>()

  const {
    control,
    handleSubmit: handleSubmitFiltro,
    resetField: resetFieldFiltro
  } = useForm<SchemaFiltroGrupos>({
    resolver: zodResolver(schemaFiltroGrupos)
  })

  const columnas: GridColDef<SectorComercialGrupoViewDTO>[] = [
    {
      headerName: 'Grupo',
      field: SectorComercialGrupoViewDTOFields.NombreGrupo,
      flex: 3
    },
    {
      headerName: 'Sistema',
      field: SectorComercialGrupoViewDTOFields.NombreSistema,
      flex: 2
    },
    {
      headerName: 'País',
      field: SectorComercialGrupoViewDTOFields.NombrePais,
      flex: 1,
      renderCell: ({row: group}) => {
        return (
          <Chip
            size='small'
            label={group[SectorComercialGrupoViewDTOFields.NombrePais]}
            avatar={
              <CountryAvatar countryCode={group[SectorComercialGrupoViewDTOFields.CodPais]}/>
            }
          />
        )
      }
    }

    // {
    //     headerName: 'Circuito',
    //     field: SectorComercialGrupoViewDTOFields.CodSistema,
    //     flex: 4
    // }
  ]

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()

  const setFilterAndSearch = (data?: SchemaFiltroGrupos) => {
    return HttpSectorComercialABM.getGrupos(sector[EntityWithIdFields.Id], data).then(groups => {
      setGroups(groups)
      const selectedGroups = groups
        .filter(group => group[SectorComercialGrupoViewDTOFields.PerteneceAGrupo])
        .map(group => group[SectorComercialGrupoViewDTOFields.IdGrupo])
      setRowSelectionModel(prev => [...new Set([...selectedGroups, ...(prev || [])])])
    })
  }

  const onCancelHandler = () => {
    onClose()
  }

  const onSubmitWrapper = () => {
    return new Promise<void>((resolve, reject) => {
      HttpSectorComercialABM.actualizarGrupos(sector[EntityWithIdFields.Id], [
        ...((rowSelectionModel as number[]) || [])
      ]).then(() => {
        setTimeout(onClose, 500)
        resolve()
      })
    })
  }

  useEffect(() => {
    setFilterAndSearch()
  }, [])

  const onFilterChange = () => {
    handleSubmitFiltro(setFilterAndSearch)()
  }

  return (
    <>
      <DialogTitle>{'Grupos de Sector Comercial'}</DialogTitle>
      <Stack spacing={2}>
        <DialogContent>
          <DialogContentText>
            {`Seleccione los grupos asociados a ${sector[SectorComercialViewDTOFields.Nombre]}.`}
          </DialogContentText>
        </DialogContent>
        <Box
          sx={{
            height: '100%'
          }}
        >
          <DataGrid
            keepNonExistentRowsSelected
            hideFooterPagination
            columns={columnas}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            rows={groups ? groups : []}
            getRowId={row => row[SectorComercialGrupoViewDTOFields.IdGrupo]}
            density='compact'
            slots={{
              columnMenu: (props: GridColumnMenuProps) => {
                const {
                  colDef: {field}
                } = props

                if (field === SectorComercialGrupoViewDTOFields.NombreSistema) {
                  return (
                    <Stack
                      direction='row'
                      spacing={2}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 2
                      }}
                    >
                      <ControlledAutocomplete
                        control={control}
                        name={SectorComercialGruposFilterDTOFields.CodsMensajeTipo}
                        valueField='id'
                        loadOptions={HttpSystems.getSystemTypesByUser}
                        label='Sistema'
                        multiple
                      />
                      <Button type='submit' onClick={onFilterChange}>
                        Buscar
                      </Button>
                      <Tooltip title='Limpiar filtro'>
                        <IconButton
                          onClick={() => {
                            resetFieldFiltro(
                              SectorComercialGruposFilterDTOFields.CodsMensajeTipo
                            )
                            onFilterChange() // search on filter reset
                          }}
                        >
                          <FilterAltOff/>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )
                }

                if (field === SectorComercialGrupoViewDTOFields.NombrePais) {
                  return (
                    <Stack
                      direction='row'
                      spacing={2}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 2
                      }}
                    >
                      <ControlledAutocomplete
                        control={control}
                        name={SectorComercialGruposFilterDTOFields.CodsPais}
                        valueField='id'
                        loadOptions={HttpSystems.getSystemPrincipalsByUser}
                        label='País'
                        multiple
                      />
                      <Button type='submit' onClick={onFilterChange}>
                        Buscar
                      </Button>
                      <Tooltip title='Limpiar filtro'>
                        <IconButton
                          onClick={() => {
                            resetFieldFiltro(SectorComercialGruposFilterDTOFields.CodsPais)
                            onFilterChange() // search on filter reset
                          }}
                        >
                          <FilterAltOff/>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )
                }

                return <></>
              },
              columnMenuIcon: FilterAlt
            }}
          />
        </Box>
      </Stack>
      <DialogActions>
        <Button variant='text' color='primary' onClick={onCancelHandler}>
          Cancelar
        </Button>
        <Button variant='contained' color='primary' promise={onSubmitWrapper}>
          Guardar
        </Button>
      </DialogActions>
    </>
  )
}

export default EditSectorComercial
